import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ActionCreators from "../../actions";
import { setLyftReportData, setTuroReportData, setOtherReportData } from "../../utils/functions";
import { getCertificateFontDownloads } from "../../utils/certificateFontDownloads";
import "./certificateDownloadButton.css";
import moment from "moment";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
  Font,
  Svg,
  Path,
  Line,
} from "@react-pdf/renderer";

import kalamFont from "../../font/Kalam-Light.ttf";
import "../../font/registerPatrickHandAndCaveatPdf";
import LatoBold from "../../font/Lato-Bold.ttf";
import LatoBlack from "../../font/Lato-Black.ttf";
import LatoLight from "../../font/Lato-Light.ttf";

Font.register({ family: "kalamFont", format: "truetype", src: kalamFont });
Font.register({ family: "LatoBold", format: "truetype", src: LatoBold });
Font.register({ family: "LatoBlack", format: "truetype", src: LatoBlack });
Font.register({ family: "LatoLight", format: "truetype", src: LatoLight });

const inspectionPointColumns = [
  [
    { label: "HEADLIGHTS", aliases: ["headlight", "headlights"] },
    { label: "TAIL LIGHTS", aliases: ["tail light", "tail lights"] },
    {
      label: "TURN INDICATOR LIGHTS",
      aliases: ["turn indicator", "turn signal", "indicator lights"],
    },
    { label: "STOP LIGHTS", aliases: ["stop light", "brake light", "brake lights"] },
    {
      label: "FOOT BRAKES",
      note: "(Pads/Shoes thickness)",
      aliases: ["foot brake", "foot brakes", "brakes - shoes", "brake shoes"],
    },
    {
      label: "EMERGENCY/PARKING BRAKE",
      aliases: ["emergency brake", "parking brake"],
    },
    { label: "STEERING MECHANISM", aliases: ["steering"] },
    { label: "WINDSHIELD", aliases: ["windshield"] },
    {
      label: "SAFETY BELTS FOR DRIVERS & PASSENGERS",
      aliases: ["safety belt", "seat belt"],
    },
    {
      label: "IS VEHICLE TAXI OR COMMERCIALLY WRAPPED?",
      answerType: "yesNo",
      aliases: [],
    },
  ],
  [
    {
      label: "REAR WINDOW & OTHER GLASS",
      aliases: ["rear window", "other glass"],
    },
    { label: "WINDSHIELD WIPERS", aliases: ["wiper", "wipers"] },
    {
      label: "FRONT SEAT ADJUSTMENT",
      aliases: ["front seat adjustment", "seat adjustment"],
    },
    { label: "DOORS", note: "(Open/Close/Lock)", aliases: ["door", "doors"] },
    { label: "HORN", aliases: ["horn"] },
    { label: "SPEEDOMETER", aliases: ["speedometer"] },
    { label: "BUMPERS", aliases: ["bumper", "bumpers"] },
    {
      label: "MUFFLER AND EXHAUST SYSTEM",
      aliases: ["muffler", "exhaust"],
    },
    { label: "TIRES, INCL TREAD DEPTH", aliases: ["tire", "tires", "tread depth"] },
    {
      label: "INTERIOR & EXTERIOR REAR VIEW MIRRORS",
      aliases: [
        "interior exterior rear view mirrors",
        "interior and exterior rear view mirrors",
        "interior exterior rearview mirrors",
        "interior and exterior rearview mirrors",
        "interior exterior mirrors",
        "rear view mirror",
        "rear view mirrors",
        "rearview mirror",
        "rearview mirrors",
        "mirrors",
      ],
    },
  ],
];

const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getCheckStatus = (checks = [], aliases = []) => {
  const normalizedAliases = aliases.map(normalize);
  const matchedCheck = checks.find((check) => {
    const checkName = normalize(check?.Check?.name || check?.name);
    if (!checkName) return false;
    return normalizedAliases.some(
      (alias) => checkName.includes(alias) || alias.includes(checkName)
    );
  });

  if (!matchedCheck) return null;
  const status = String(matchedCheck.status).toLowerCase();
  return matchedCheck.status === true ||
    matchedCheck.status === 1 ||
    status === "true" ||
    status === "1" ||
    status === "pass"
    ? "pass"
    : "fail";
};

const formatDate = (value, format = "MM/DD/YYYY") =>
  value ? moment(value).format(format) : "";

const getFullName = (data = {}) =>
  `${data?.Username || ""} ${data?.UserlastName || ""}`.trim();

const getInspectorName = (data = {}) =>
  `${data?.Inspectorname || ""} ${data?.InspectorlastName || ""}`.trim() ||
  getFullName(data);

const humanTickPaths = [
  "M4 12.5C6.3 15.4 8.1 17.7 9.2 18.7C12.5 13.5 16.1 8.8 21 4.4",
  "M3.5 13.2C5.9 14.9 7.6 17.1 9.5 19.2C12.2 13.2 16.6 7.7 20.6 5.2",
  "M4.2 11.8C6.7 14.1 8.3 16.6 10.1 18.1C13.4 12.2 16.7 8.1 21.4 4.8",
  "M3.9 12.9C6 15.2 7.8 16.7 9.7 18.8C12.3 13.6 15.9 7.9 20.8 5",
  "M4.1 13.4C6.5 15 8.5 17.8 9.6 18.9C12.7 12.9 16.9 8.2 21.2 4.7",
];

const styles = StyleSheet.create({
  body: {
    color: "#000",
    fontFamily: "Helvetica",
    padding: 8,
  },
  pageFrame: {
    padding: 10,
  },
  header: {
    backgroundColor: "#000",
    height: 92,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 12,
    paddingTop: 2,
    paddingBottom: 8,
  },
  title: {
    color: "#fff",
    fontFamily: "LatoLight",
    fontSize: 34,
    lineHeight: 1,
  },
  formUpdated: {
    color: "#fff",
    fontFamily: "LatoLight",
    fontSize: 12,
    alignSelf: "flex-end",
    marginRight: -5,
  },
  sectionLabel: {
    alignItems: "center",
    backgroundColor: "#dddddd",
    flexDirection: "row",
    height: 24,
    paddingHorizontal: 28,
  },
  sectionLabelText: {
    fontSize: 12,
    textTransform: "uppercase",
  },
  sectionArrow: {
    width: 11,
    height: 9,
    marginLeft: 6,
  },
  operatorSection: {
    backgroundColor: "#eeeeee",
    flexDirection: "row",
    minHeight: 78,
    paddingBottom: 36,
    paddingHorizontal: 10,
    paddingTop: 26,
  },
  operatorNameField: {
    flex: 1.65,
    marginRight: 30,
  },
  operatorPhoneField: {
    flex: 1,
  },
  fieldValue: {
    borderBottomColor: "#111",
    borderBottomWidth: 2,
    justifyContent: "flex-end",
    minHeight: 22,
    paddingBottom: 2,
  },
  fieldValueText: {
    fontSize: 15,
    lineHeight: 1,
  },
  fieldLabel: {
    fontFamily: "LatoBlack",
    fontSize: 10,
    marginTop: 2,
    textTransform: "uppercase",
  },
  inspectionSection: {
    paddingBottom: 22,
    paddingHorizontal: 10,
    paddingTop: 24,
  },
  inspectionTitle: {
    fontFamily: "LatoBold",
    fontSize: 14,
    marginBottom: 12,
  },
  inspectionGrid: {
    flexDirection: "row",
  },
  inspectionColumn: {
    flex: 1,
  },
  inspectionColumnLeft: {
    marginRight: 28,
  },
  inspectionPoint: {
    alignItems: "flex-end",
    flexDirection: "row",
    minHeight: 21,
  },
  pointLabel: {
    flexShrink: 0,
    fontSize: 12,
    lineHeight: 1.1,
  },
  pointLabelBold: {
    fontFamily: "LatoBold",
  },
  pointNote: {
    fontSize: 10,
  },
  dottedLine: {
    borderBottomColor: "#111",
    borderBottomStyle: "dotted",
    borderBottomWidth: 1,
    flexGrow: 1,
    marginBottom: 1,
    marginLeft: 4,
    marginRight: 0,
    minWidth: 16,
  },
  option: {
    alignItems: "center",
    flexDirection: "row",
    marginLeft: 7,
  },
  optionPass: {
    marginLeft: 0,
  },
  box: {
    borderColor: "#111",
    borderWidth: 1.7,
    height: 14,
    marginRight: 6,
    position: "relative",
    textAlign: "center",
    width: 14,
  },
  humanTick: {
    height: 22,
    left: -3,
    position: "absolute",
    top: -7,
    width: 22,
  },
  optionText: {
    fontSize: 10,
  },
  resultsSection: {
    alignSelf: "center",
    marginBottom: 28,
    width: 700,
    marginTop: 10,
  },
  resultsTitle: {
    alignItems: "stretch",
    backgroundColor: "#777170",
    height: 24,
    justifyContent: "center",
  },
  resultsTitleText: {
    color: "#fff",
    fontFamily: "LatoBold",
    fontSize: 14,
    lineHeight: 1,
    textAlign: "center",
    width: "100%",
  },
  resultsBody: {
    backgroundColor: "#eeeeee",
    justifyContent: "center",
    minHeight: 142,
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
  failNotice: {
    fontSize: 13,
    marginBottom: 20,
    textAlign: "center",
  },
  resultStatusRow: {
    alignItems: "flex-start",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  resultStatus: {
    alignItems: "center",
    fontFamily: "LatoBold",
    fontSize: 31,
    justifyContent: "center",
    minHeight: 58,
    position: "relative",
    textAlign: "center",
    width: 150,
  },
  resultStatusPass: {
    marginLeft: -22,
    marginTop: -10,
  },
  resultStatusFail: {
    marginRight: -22,
    marginTop: -10,
  },
  resultStatusMarker: {
    alignItems: "center",
    height: 86,
    justifyContent: "center",
    position: "relative",
    width: 150,
  },
  resultStatusText: {
    fontFamily: "LatoBold",
    fontSize: 31,
    left: 28,
    position: "absolute",
    textAlign: "center",
    top: 24,
    width: 82,
  },
  handDrawnStatusCircle: {
    height: 86,
    left: 0,
    position: "absolute",
    top: 0,
    width: 150,
  },
  resultDate: {
    alignItems: "center",
    marginTop: 88,
    width: 220,
  },
  resultDateValue: {
    fontSize: 15,
    minHeight: 20,
    textAlign: "center",
    width: 190,
  },
  resultDateLine: {
    position: "relative",
    width: 190,
  },
  resultDateDash: {
    bottom: 0,
    height: 2,
    left: 0,
    position: "absolute",
    width: 190,
  },
  resultDateLabel: {
    fontSize: 13,
    marginTop: 2,
  },
  inspectorSection: {
    backgroundColor: "#eeeeee",
    paddingBottom: 16,
    paddingHorizontal: 10,
    paddingTop: 3,
  },
  inspectorRow: {
    alignItems: "flex-end",
    flexDirection: "row",
    marginBottom: 1,
  },
  inspectorField: {
    marginRight: 28,
  },
  noMarginRight: {
    marginRight: 0,
  },
  wMileage: {
    width: 150,
  },
  wPlateState: {
    width: 148,
  },
  wPlateNumber: {
    width: 170,
  },
  wVin: {
    flex: 1,
  },
  wMake: {
    width: 185,
  },
  wModel: {
    width: 180,
  },
  wYear: {
    width: 120,
  },
  wDoors: {
    flex: 1,
  },
  wSticker: {
    width: 310,
  },
  wStickerDate: {
    flex: 1,
  },
  wCompany: {
    width: 310,
  },
  wAddress: {
    flex: 1,
  },
  wInspectorName: {
    width: 310,
  },
  wSignature: {
    flex: 1,
  },
  wStateNumber: {
    width: 220,
  },
  signatureImage: {
    position: "absolute",
    bottom: -8,
    left: 0,
    height: 46,
    width: 210,
    objectFit: "contain",
  },
});

const Field = ({ label, value, style, handwritingStyle }) => {
  const fieldStyle = Array.isArray(style)
    ? [styles.inspectorField, ...style]
    : [styles.inspectorField, style];

  return (
    <View style={fieldStyle}>
      <View style={styles.fieldValue}>
        <Text style={[styles.fieldValueText, handwritingStyle]}>
          {value || ""}
        </Text>
      </View>
      <Text style={styles.fieldLabel}>{label}</Text>
    </View>
  );
};

const SectionLabel = ({ children }) => (
  <View style={styles.sectionLabel}>
    <Text style={styles.sectionLabelText}>{children}</Text>
    <Svg style={styles.sectionArrow} viewBox="0 0 12 14">
      <Path
        d="M6 1 L6 12"
        stroke="#111111"
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d="M6 12 L2.6 8.6"
        stroke="#111111"
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d="M6 12 L9.4 8.6"
        stroke="#111111"
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
    </Svg>
  </View>
);

const HumanTick = ({ tickIndex = 0 }) => (
  <Svg style={styles.humanTick} viewBox="0 0 24 24">
    <Path
      d={humanTickPaths[tickIndex % humanTickPaths.length]}
      stroke="#000"
      strokeWidth={2.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </Svg>
);

const Box = ({ checked, tickIndex }) => (
  <View style={styles.box}>
    {checked ? <HumanTick tickIndex={tickIndex} /> : null}
  </View>
);

const InspectionPoint = ({ item, checks, tickIndex }) => {
  const status = getCheckStatus(checks, item.aliases);
  const passLabel = item.answerType === "yesNo" ? "YES" : "PASS";
  const failLabel = item.answerType === "yesNo" ? "NO" : "FAIL";

  return (
    <View style={styles.inspectionPoint}>
      <Text style={styles.pointLabel}>
        <Text style={styles.pointLabelBold}>{item.label}</Text>
        {item.note ? <Text style={styles.pointNote}> {item.note}</Text> : null}
      </Text>
      <View style={styles.dottedLine} />
      <View style={[styles.option, styles.optionPass]}>
        <Box checked={status === "pass"} tickIndex={tickIndex} />
        <Text style={styles.optionText}>{passLabel}</Text>
      </View>
      <View style={styles.option}>
        <Box checked={status === "fail"} tickIndex={tickIndex + 2} />
        <Text style={styles.optionText}>{failLabel}</Text>
      </View>
    </View>
  );
};

const HandDrawnStatusCircle = () => (
  <Svg style={styles.handDrawnStatusCircle} viewBox="0 0 112 73">
    <Path
      d="M52.1927 2.90815C34.1317 2.90815 15.3734 8.31886 6.56927 25.4371C3.77005 30.8798 1.5 32.7484 1.5 38.8919C1.5 44.4471 5.2551 50.3534 10.1902 53.2854C14.6663 55.9447 15.5908 61.4646 20.3287 63.7676C25.118 66.0956 26.9011 68.1651 32.5593 69.1652C42.084 70.8488 57.582 73.3293 66.6763 69.3999C70.4649 67.7629 77.2718 68.2962 81.4818 67.366C84.6931 66.6565 103.112 62.3328 104.253 58.4483C106.691 50.1537 115.056 36.3085 107.23 28.2532C102.352 23.2319 97.9017 18.6987 91.2985 15.5807C86.0695 13.1115 82.0045 12.4543 76.8149 10.6525C70.3868 8.42063 63.9661 4.31621 57.262 4.31621C51.9098 4.31621 47.9811 4.76555 43.5025 1.50009"
      stroke="#000"
      strokeWidth={3}
      strokeLinecap="round"
      fill="none"
    />
  </Svg>
);

const ResultStatus = ({ selected, children, style }) => (
  <View style={[styles.resultStatus, style]}>
    <View style={styles.resultStatusMarker}>
      {selected ? <HandDrawnStatusCircle /> : null}
      <Text style={styles.resultStatusText}>{children}</Text>
    </View>
  </View>
);

function UberCertificateDownLoad({
  firstHalfData,
  certificateData,
  handwritingFont = "kalamFont",
  fileName = "Vehicle-certificate.pdf",
  linkLabel = "Download pdf",
}) {
  const handwritingStyle = { fontFamily: handwritingFont };
  const finalStatus = String(certificateData?.finalStatus || "").toLowerCase();
  const inspectorName = getInspectorName(certificateData);

  return (
    <div className="App">
      <PDFDownloadLink
        document={
          <Document>
            <Page size="A3" style={styles.body} wrap={false}>
              <View style={styles.pageFrame}>
                <View style={styles.header}>
                  <Text style={styles.title}>Vehicle Inspection</Text>
                  <Text style={styles.formUpdated}>FORM UPDATED 02.04.19</Text>
                </View>

                <SectionLabel>TO BE COMPLETED BY VEHICLE OPERATOR</SectionLabel>

                <View style={styles.operatorSection}>
                  <View style={styles.operatorNameField}>
                    <View style={styles.fieldValue}>
                      <Text style={[styles.fieldValueText, handwritingStyle]}>
                        {getFullName(certificateData)}
                      </Text>
                    </View>
                    <Text style={styles.fieldLabel}>FULL NAME</Text>
                  </View>
                  <View style={styles.operatorPhoneField}>
                    <View style={styles.fieldValue}>
                      <Text style={[styles.fieldValueText, handwritingStyle]}>
                        {certificateData?.Userphone || ""}
                      </Text>
                    </View>
                    <Text style={styles.fieldLabel}>PHONE NUMBER</Text>
                  </View>
                </View>

                <View style={styles.inspectionSection}>
                  <Text style={styles.inspectionTitle}>INSPECTION POINTS</Text>
                  <View style={styles.inspectionGrid}>
                    {inspectionPointColumns.map((column, columnIndex) => (
                      <View
                        key={columnIndex}
                        style={[
                          styles.inspectionColumn,
                          columnIndex === 0 ? styles.inspectionColumnLeft : null,
                        ]}
                      >
                        {column.map((item, itemIndex) => (
                          <InspectionPoint
                            item={item}
                            checks={firstHalfData}
                            tickIndex={columnIndex * 10 + itemIndex}
                            key={item.label}
                          />
                        ))}
                      </View>
                    ))}
                  </View>
                </View>

                <View style={styles.resultsSection}>
                  <View style={styles.resultsTitle}>
                    <Text style={styles.resultsTitleText}>
                      VEHICLE INSPECTION RESULTS (Inspector to circle)
                    </Text>
                  </View>
                  <View style={styles.resultsBody}>
                    <Text style={styles.failNotice}>
                      {"Any markings on the 'FAIL' side will automatically fail inspection. Taxi's and commercially wrapped\n" +
                        "vehicles will automatically fail inspection. All fields of the vehicle inspection form must be completed by\n" +
                        "your inspector to be accepted upon upload to your Uber driver account."}
                    </Text>
                    <View style={styles.resultStatusRow}>
                      <ResultStatus
                        selected={finalStatus === "pass"}
                        style={styles.resultStatusPass}
                      >
                        PASS
                      </ResultStatus>
                      <View style={styles.resultDate}>
                        <View style={styles.resultDateLine}>
                          <Text style={[styles.resultDateValue, handwritingStyle]}>
                            {formatDate(certificateData?.reviewedDate)}
                          </Text>
                          <Svg
                            style={styles.resultDateDash}
                            width={190}
                            height={2}
                            viewBox="0 0 190 2"
                          >
                            <Line
                              x1={0}
                              y1={1}
                              x2={190}
                              y2={1}
                              stroke="#111"
                              strokeWidth={2}
                              strokeDasharray="8,4"
                            />
                          </Svg>
                        </View>
                        <Text style={styles.resultDateLabel}>Inspection Date</Text>
                      </View>
                      <ResultStatus
                        selected={finalStatus === "fail"}
                        style={styles.resultStatusFail}
                      >
                        FAIL
                      </ResultStatus>
                    </View>
                  </View>
                </View>

                <SectionLabel>TO BE COMPLETED BY INSPECTOR</SectionLabel>

                <View style={styles.inspectorSection}>
                  <View style={styles.inspectorRow}>
                    <Field
                      label="VEHICLE MILEAGE"
                      value={certificateData?.milage}
                      style={styles.wMileage}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="LICENSE PLATE STATE"
                      value={certificateData?.registrationState}
                      style={styles.wPlateState}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="LICENSE PLATE NUMBER"
                      value={certificateData?.licensePlateNumber}
                      style={styles.wPlateNumber}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="VIN"
                      value={certificateData?.vin}
                      style={[styles.wVin, styles.noMarginRight]}
                      handwritingStyle={handwritingStyle}
                    />
                  </View>

                  <View style={styles.inspectorRow}>
                    <Field
                      label="VEHICLE MAKE"
                      value={certificateData?.make}
                      style={styles.wMake}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="VEHICLE MODEL"
                      value={certificateData?.model}
                      style={styles.wModel}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="VEHICLE YEAR"
                      value={certificateData?.year}
                      style={styles.wYear}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="NUMBER OF DOORS"
                      value={certificateData?.vehicleDoors}
                      style={[styles.wDoors, styles.noMarginRight]}
                      handwritingStyle={handwritingStyle}
                    />
                  </View>

                  <View style={styles.inspectorRow}>
                    <Field
                      label="HAS REGISTRATION STICKER? (YES/NO)"
                      value={
                        certificateData?.registrationSticker === undefined
                          ? ""
                          : certificateData.registrationSticker === true
                            ? "YES"
                            : "NO"
                      }
                      style={styles.wSticker}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="REGISTRATION STICKER MONTH/YEAR (MM/YY)"
                      value={certificateData?.RegistrationDate}
                      style={[styles.wStickerDate, styles.noMarginRight]}
                      handwritingStyle={handwritingStyle}
                    />
                  </View>

                  <View style={styles.inspectorRow}>
                    <Field
                      label="INSPECTOR COMPANY"
                      value="Chex Inc."
                      style={styles.wCompany}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field
                      label="INSPECTOR ADDRESS"
                      value={certificateData?.stateAddress}
                      style={[styles.wAddress, styles.noMarginRight]}
                      handwritingStyle={handwritingStyle}
                    />
                  </View>

                  <View style={[styles.inspectorRow, { marginBottom: 0 }]}>
                    <Field
                      label="INSPECTOR NAME"
                      value={inspectorName}
                      style={styles.wInspectorName}
                      handwritingStyle={handwritingStyle}
                    />
                    <View style={[styles.inspectorField, styles.wSignature]}>
                      <View style={styles.fieldValue}>
                        {certificateData?.Inspectorsignature ? (
                          <Image
                            style={styles.signatureImage}
                            source={`${process.env.REACT_APP_AWS_S3_LINK}/${certificateData.Inspectorsignature}?x-request=xhr`}
                          />
                        ) : null}
                      </View>
                      <Text style={styles.fieldLabel}>INSPECTOR SIGNATURE</Text>
                    </View>
                    <Field
                      label="STATE CERTIFICATION NUMBER"
                      value={certificateData?.stateNumber}
                      style={[styles.wStateNumber, styles.noMarginRight]}
                      handwritingStyle={handwritingStyle}
                    />
                  </View>
                </View>
              </View>
            </Page>
          </Document>
        }
        fileName={fileName}
      >
        {({ loading }) => (
          <>
            {loading ? (
              <div className="certificate-download-button is-loading" aria-live="polite">
                <span className="certificate-download-spinner" />
                <div>Preparing PDF</div>
              </div>
            ) : (
              <div
                className="certificate-download-button"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    marginBottom: "20px",
                    marginTop: "20px",
                    color: "#3FA9FF",
                    fontWeight: "bold",
                    marginRight: "5px",
                    cursor: "pointer",
                  }}
                >
                  {linkLabel}
                </div>
                <i className="fa fa-download" />
              </div>
            )}
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
}


const UberCertificateDownLoadConnected = (props) => {
  const { id, companyId, reportData, checksData } = props;
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      props.getVehicleCertificate({ id, companyId }, props.setLoading, "uber");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return (
    <div className="certificate-download-group">
      {!reportData ? (
        <div className="certificate-download-button is-loading" aria-live="polite">
          <span className="certificate-download-spinner" />
          <div>Loading certificate data</div>
        </div>
      ) : (
        getCertificateFontDownloads(reportData?.certificateFont).map((cfg) => (
          <UberCertificateDownLoad
            key={cfg.handwritingFont}
            reportData={reportData} certificateData={reportData}
            checksData={checksData} checks={checksData} vehicleChecksArray={checksData} firstHalfData={checksData}
            handwritingFont={cfg.handwritingFont}
            fileName={cfg.fileName}
            linkLabel={cfg.linkLabel}
          />
        ))
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const sliceData = state && state.vehicleInstruction && state.vehicleInstruction.uberVehicleCertificate;
  return {
    reportData: sliceData,
    checksData: setOtherReportData(sliceData && sliceData.Files),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(UberCertificateDownLoadConnected);
